import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { CalendarIcon, CalendarOutlinedActiveIcon, ArrowLeftIcon, PlusIcon, ArrowRightIcon } from '~/assets/icons';
import dayjs from 'dayjs';
import { BaseHeaderTitle, BaseTab, BaseTabList } from '~/components/common';
import { useTranslation } from 'react-i18next';
import Calendar from '~/components/schedules/calendar/Calendar';
import { SlotInfo, Views, ViewsProps } from 'react-big-calendar';
import { CalendarViewMode, ScheduleMode } from '~/utils/types/schedule';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { scheduleActions } from '~/thunks/schedule/scheduleSlice';
import { handleGetDayEvents, handleGetEvents } from '~/thunks/schedule/scheduleThunk';
import EditEventDialog from '~/components/schedules/edit-event-dialog/EditEventDialog';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { IEditEventPayload, IScheduleEvent } from '~/utils/interface/schedule';
import { catchError, handleApiError, notify } from '~/utils/helper';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import { SCHEDULE_MODE_FULL, SCHEDULE_MODE_MYSELF, VIEW_MODE_MONTH } from '~/utils/constants/schedules';
import scheduleEventApi from '~/apis/schedule';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';

const views: ViewsProps = {
  month: true,
  week: true,
  day: true,
};

const Schedule = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/schedule', 'admin/common']);
  //#endregion Declare Hook

  //#region Selector
  const {
    currentTime,
    scheduleMode,
    calendarViewMode,
    events,
    dayEvents,
    selectedEventId,
    openAlertDelete,
    needRefreshData,
  } = useAppSelector((state) => state.schedule);
  //#endregion Selector

  //#region Declare State
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editEventDialogMode, setEditEventDialogMode] = useState<'create' | 'edit'>(DIALOG_MODE_CREATE);
  const [editData, setEditData] = useState<IEditEventPayload | undefined>(undefined);
  //#endregion Declare State

  //#region Implement Hook
  const permissions = usePermission();
  const handleSetOpen = useCallback(
    (open: boolean, needRefreshData?: boolean) => {
      setOpenEditDialog(open);
      if (needRefreshData) {
        dispatch(scheduleActions.changeNeedRefreshData(needRefreshData));
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const startDay = dayjs(currentTime).startOf(calendarViewMode).format('YYYY-MM-DD');
    const endDay = dayjs(currentTime).endOf(calendarViewMode).format('YYYY-MM-DD');
    if (calendarViewMode === VIEW_MODE_MONTH || scheduleMode === SCHEDULE_MODE_MYSELF) {
      dispatch(
        handleGetEvents({
          mode: scheduleMode,
          startDay,
          endDay,
        })
      );
    } else {
      dispatch(
        handleGetDayEvents({
          mode: scheduleMode,
          startDay,
          endDay,
        })
      );
    }
  }, [dispatch, scheduleMode, currentTime, calendarViewMode, needRefreshData]);

  const timeValue = useMemo(() => {
    switch (calendarViewMode) {
      case Views.WEEK:
        const startOfWeek = dayjs(currentTime).startOf('week').add(1, 'day').format('dddd, MMMM DD');
        return startOfWeek;
      case Views.DAY:
        return dayjs(currentTime).format('dddd, MMMM DD');
      default:
        return dayjs(currentTime).format('MMMM YYYY');
    }
  }, [calendarViewMode, currentTime]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(scheduleActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const onDeleteSelectedEvent = useCallback(async () => {
    try {
      if (selectedEventId) {
        await scheduleEventApi.deleteEvent({
          ids: [selectedEventId],
          isDeleteAll: false,
        });
      }
      notify('success', t('deleteSuccessMessage'));
      dispatch(scheduleActions.changeNeedRefreshData(true));
      dispatch(scheduleActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, t, selectedEventId]);
  const memoEvents = useMemo(() => events, [events]);

  const onSelectEvent = useCallback(async (event: IScheduleEvent) => {
    setEditEventDialogMode(DIALOG_MODE_EDIT);
    try {
      const selectedEvent = (await scheduleEventApi.getEvent(event.id)).data.data;
      setEditData(selectedEvent);
      setOpenEditDialog(true);
    } catch (error: unknown) {
      handleApiError(error);
    }
  }, []);

  const onSelectSlot = useCallback((slotInfo: SlotInfo) => {
    if (slotInfo.action === 'doubleClick') {
      setEditEventDialogMode(DIALOG_MODE_CREATE);
      setEditData(undefined);
      setOpenEditDialog(true);
    }
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleChangeScheduleMode = (_event: React.ChangeEvent<{}>, newValue: ScheduleMode) => {
    dispatch(scheduleActions.changeScheduleMode(newValue));
  };
  const handleChangeCalendarView = (_event: React.ChangeEvent<{}>, newValue: CalendarViewMode): void => {
    dispatch(scheduleActions.changeCalendarViewMode(newValue));
  };
  //#endregion Handle Function

  return (
    <Box>
      <Box className='flex flex-row'>
        <BaseHeaderTitle title={t('title')} />
        {permissions[PERMISSIONS.schedule_create] && (
          <Button
            onClick={() => {
              setEditData(undefined);
              setEditEventDialogMode(DIALOG_MODE_CREATE);
              setOpenEditDialog(true);
            }}
            className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-lg'
            variant='contained'
            disableElevation
            startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
          >
            {t('addSchedule')}
          </Button>
        )}
      </Box>
      <Box className='mt-[20px] mb-[20px] flex flex-col items-start lg:flex-row lg:items-center'>
        <Box className='flex flex-row flex-1'>
          <Box className='flex flex-row items-center'>
            <Box className='flex flex-row'>
              <IconButton
                color='primary'
                className='pl-[6px] pr-[4.5px] py-[7.5px] rounded-l-lg rounded-r-none bg-[#007AFC] hover:bg-[#007AFC]'
                onClick={() => dispatch(scheduleActions.goPreviousTime())}
              >
                <ArrowLeftIcon className='w-[13px] h-[13px]' />
              </IconButton>
              <IconButton
                color='primary'
                className='pl-[4.5px] pr-[6px] py-[7.5px] rounded-r-lg rounded-l-none bg-[#007AFC] hover:bg-[#007AFC]'
                onClick={() => dispatch(scheduleActions.goNextTime())}
              >
                <ArrowRightIcon className='w-[13px] h-[13px]' />
              </IconButton>
            </Box>
            <Button
              variant='outlined'
              className='text-[#797D8A] border border-[#E5EAEF] hover:border-[#E5EAEF] rounded-lg text-[14px]/[16.8px] font-medium ml-[8px] h-[28px] px-[9px] py-[5.5px]'
              onClick={() => dispatch(scheduleActions.goToday())}
            >
              {t('today')}
            </Button>
          </Box>
          <Box className='flex flex-1 justify-center min-w-[220px]'>
            <Typography className='text-[20px]/[26.04px] text-[#42485B] font-medium' variant='h5'>
              {timeValue}
            </Typography>
          </Box>
        </Box>
        <Box className='flex flex-col items-start mt-[8px] lg:mt-0 lg:flex-row'>
          <Box>
            <BaseTabList
              value={scheduleMode}
              onChange={handleChangeScheduleMode}
              centered
              TabIndicatorProps={{ style: { backgroundColor: 'transparent' } }}
            >
              <BaseTab
                key={SCHEDULE_MODE_FULL}
                value={SCHEDULE_MODE_FULL}
                icon={
                  scheduleMode === SCHEDULE_MODE_FULL ? (
                    <CalendarOutlinedActiveIcon className='w-[17px] h-[17px]' />
                  ) : (
                    <CalendarIcon className='w-[17px] h-[17px]' />
                  )
                }
                iconPosition='start'
                label={t('companySchedule')}
              />
              <BaseTab
                key={SCHEDULE_MODE_MYSELF}
                value={SCHEDULE_MODE_MYSELF}
                icon={
                  scheduleMode === SCHEDULE_MODE_MYSELF ? (
                    <CalendarOutlinedActiveIcon className='w-[17px] h-[17px]' />
                  ) : (
                    <CalendarIcon className='w-[17px] h-[17px]' />
                  )
                }
                iconPosition='start'
                label={t('mySchedule')}
              />
            </BaseTabList>
          </Box>
          <Box className={`mt-[8px] lg:ml-[8px] lg:mt-0`}>
            <BaseTabList
              value={calendarViewMode}
              onChange={handleChangeCalendarView}
              centered
              TabIndicatorProps={{ style: { backgroundColor: 'transparent' } }}
            >
              {Object.keys(views).map((view: string, index: number) => {
                return <BaseTab value={view} key={index} label={t(`datetime.${view}`, { ns: 'admin/common' })} />;
              })}
            </BaseTabList>
          </Box>
        </Box>
      </Box>
      <Box className='h-[calc(100vh-260px)] overflow-auto'>
        <Calendar
          view={calendarViewMode}
          date={currentTime}
          events={memoEvents}
          dayEvents={dayEvents}
          onSelectEvent={onSelectEvent}
          onSelectSlot={onSelectSlot}
          scheduleMode={scheduleMode}
        />
      </Box>

      <EditEventDialog open={openEditDialog} setOpen={handleSetOpen} mode={editEventDialogMode} data={editData} />

      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedEvent}
      />
    </Box>
  );
};

export default Schedule;
